/**
 * SEO Utilities
 *
 * This module provides utility functions for generating SEO metadata
 * including Open Graph tags, Twitter Card tags, and structured data.
 */

/**
 * Site configuration for SEO
 * 
 * Note: This is a shared module that can be imported on both client and server.
 * Using hardcoded values to avoid process.env in client-side code.
 */
export const SITE_CONFIG = {
  name: "AI 내동생",
  title: "AI 내동생 - AI가 도와주는 맞춤 학습",
  description:
    "개념을 설명하면 AI가 피드백을 제공하고, 1:1 맞춤 학습을 도와주는 혁신적인 교육 플랫폼입니다.",
  url: "https://aimybro.com",
  ogImage: "https://aimybro.com/og-image.png",
  twitterHandle: "@aimybro", // 트위터 계정이 있다면 변경하세요
  keywords: [
    "AI 학습",
    "개념 학습",
    "AI 튜터",
    "맞춤 학습",
    "온라인 교육",
    "학습 도우미",
    "AI 교육",
  ],
};

/**
 * Generate Open Graph meta tags
 */
export interface OGMetadata {
  title?: string;
  description?: string;
  url?: string;
  image?: string;
  type?: "website" | "article" | "profile";
  siteName?: string;
}

export function generateOGTags(metadata: OGMetadata = {}) {
  const {
    title = SITE_CONFIG.title,
    description = SITE_CONFIG.description,
    url = SITE_CONFIG.url,
    image = SITE_CONFIG.ogImage,
    type = "website",
    siteName = SITE_CONFIG.name,
  } = metadata;

  return [
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:url", content: url },
    { property: "og:image", content: image },
    { property: "og:type", content: type },
    { property: "og:site_name", content: siteName },
    { property: "og:locale", content: "ko_KR" },
    { property: "og:locale:alternate", content: "en_US" },
  ];
}

/**
 * Generate Twitter Card meta tags
 */
export interface TwitterMetadata {
  title?: string;
  description?: string;
  image?: string;
  card?: "summary" | "summary_large_image" | "app" | "player";
  site?: string;
  creator?: string;
}

export function generateTwitterTags(metadata: TwitterMetadata = {}) {
  const {
    title = SITE_CONFIG.title,
    description = SITE_CONFIG.description,
    image = SITE_CONFIG.ogImage,
    card = "summary_large_image",
    site = SITE_CONFIG.twitterHandle,
    creator = SITE_CONFIG.twitterHandle,
  } = metadata;

  return [
    { name: "twitter:card", content: card },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: image },
    { name: "twitter:site", content: site },
    { name: "twitter:creator", content: creator },
  ];
}

/**
 * Generate structured data (JSON-LD)
 */
export interface StructuredDataOptions {
  type: "Organization" | "WebSite" | "Article" | "BreadcrumbList";
  data: Record<string, unknown>;
}

export function generateStructuredData(options: StructuredDataOptions) {
  const { type, data } = options;

  const baseData = {
    "@context": "https://schema.org",
    "@type": type,
    ...data,
  };

  return {
    type: "application/ld+json",
    children: JSON.stringify(baseData),
  };
}

/**
 * Organization structured data
 */
export function getOrganizationStructuredData() {
  return generateStructuredData({
    type: "Organization",
    data: {
      name: SITE_CONFIG.name,
      url: SITE_CONFIG.url,
      logo: `${SITE_CONFIG.url}/favicon_squ_new.png`,
      description: SITE_CONFIG.description,
      sameAs: [
        // 소셜 미디어 계정이 있다면 추가하세요
        // "https://www.facebook.com/aimybro",
        // "https://twitter.com/aimybro",
        // "https://www.instagram.com/aimybro",
      ],
    },
  });
}

/**
 * WebSite structured data
 */
export function getWebSiteStructuredData() {
  return generateStructuredData({
    type: "WebSite",
    data: {
      name: SITE_CONFIG.name,
      url: SITE_CONFIG.url,
      description: SITE_CONFIG.description,
      potentialAction: {
        "@type": "SearchAction",
        target: {
          "@type": "EntryPoint",
          urlTemplate: `${SITE_CONFIG.url}/search?q={search_term_string}`,
        },
        "query-input": "required name=search_term_string",
      },
    },
  });
}

/**
 * Generate complete SEO meta tags for a page
 */
export interface PageMetadata {
  title?: string;
  description?: string;
  keywords?: string[];
  url?: string;
  image?: string;
  noindex?: boolean;
  nofollow?: boolean;
  canonical?: string;
}

export function generatePageMetadata(metadata: PageMetadata = {}) {
  const {
    title = SITE_CONFIG.title,
    description = SITE_CONFIG.description,
    keywords = SITE_CONFIG.keywords,
    url,
    image,
    noindex = false,
    nofollow = false,
    canonical,
  } = metadata;

  const metaTags = [
    { title },
    { name: "description", content: description },
    { name: "keywords", content: keywords.join(", ") },
    ...generateOGTags({ title, description, url, image }),
    ...generateTwitterTags({ title, description, image }),
  ];

  if (noindex || nofollow) {
    const robotsContent = [
      noindex ? "noindex" : "index",
      nofollow ? "nofollow" : "follow",
    ].join(", ");
    metaTags.push({ name: "robots", content: robotsContent });
  }

  if (canonical) {
    metaTags.push({ tagName: "link", rel: "canonical", href: canonical });
  }

  return metaTags;
}

